import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Empty, List, message, Skeleton, Space, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  MailOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import {
  fetchWorkspace,
  fetchWorkspaceMembershipsForUser,
  respondToWorkspaceInvitation,
} from "services/workspaceService";
import {
  formatDateTime,
  membershipPrivilegeNames,
  membershipRoleNames,
  membershipUserEmail,
  membershipUserName,
  readable,
  statusColor,
} from "workspaces/workspaceUtils";
import "./OlreadiaFlowPages.css";

const { Text, Title, Paragraph } = Typography;

export default function Invitations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces, selectWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [invitations, setInvitations] = useState([]);
  const [workspaceLookup, setWorkspaceLookup] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processingKey, setProcessingKey] = useState("");

  const pendingCount = useMemo(() => invitations.length, [invitations]);

  const loadInvitations = useCallback(async ({ quiet = false } = {}) => {
    if (!actorId) return;
    try {
      if (!quiet) setLoading(true);
      setError("");
      const page = await fetchWorkspaceMembershipsForUser({
        actorId,
        userId: actorId,
        size: 100,
      });
      const pending = (page.content || []).filter(
        (item) => String(item.membershipStatus || "").toUpperCase() === "PENDING",
      );
      setInvitations(pending);

      const entries = await Promise.all(
        pending
          .map((item) => item.workspaceId)
          .filter(Boolean)
          .map(async (workspaceId) => {
            try {
              return [workspaceId, await fetchWorkspace(workspaceId, actorId)];
            } catch {
              return [workspaceId, { id: workspaceId, name: "Workspace" }];
            }
          }),
      );
      setWorkspaceLookup(Object.fromEntries(entries));
    } catch (nextError) {
      setError(nextError?.message || "Could not load invitations.");
      setInvitations([]);
      setWorkspaceLookup({});
    } finally {
      setLoading(false);
    }
  }, [actorId]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const respond = async (invitation, action) => {
    if (!invitation?.workspaceId || !actorId || processingKey) return;
    const key = `${action}-${invitation.id || invitation.workspaceId}`;
    try {
      setProcessingKey(key);
      await respondToWorkspaceInvitation({
        actorId,
        workspaceId: invitation.workspaceId,
        action,
      });
      message.success(`Invitation ${action === "accept" ? "accepted" : "declined"}.`);
      if (action === "accept") {
        await refreshWorkspaces();
        await selectWorkspace(invitation.workspaceId);
      }
      await loadInvitations({ quiet: true });
      if (action === "accept") navigate(paths.workspaces);
    } catch (nextError) {
      message.error(nextError?.message || `Could not ${action} invitation.`);
    } finally {
      setProcessingKey("");
    }
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout olreadia-flow-layout--full">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><MailOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Invitations</Text>
              <Title level={2}>Workspace Invitations</Title>
              <Paragraph>
                Join workspaces where your applications, interviews, hiring conversations, and team access are managed.
              </Paragraph>
            </div>
            <Button icon={<ReloadOutlined />} onClick={() => loadInvitations()} loading={loading}>
              Refresh
            </Button>
          </section>

          <div className="olreadia-flow-toolbar">
            <Space wrap>
              <Tag className={`olreadia-flow-status is-${pendingCount ? "warn" : "good"}`}>
                {pendingCount} pending
              </Tag>
            </Space>
          </div>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load invitations"
              description={error}
              action={<Button onClick={() => loadInvitations()}>Retry</Button>}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {loading && !invitations.length ? (
            <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 4 }} /></Card>
          ) : invitations.length ? (
            <List
              className="olreadia-flow-list"
              dataSource={invitations}
              renderItem={(invitation) => {
                const workspace = invitation.workspaceId ? workspaceLookup[invitation.workspaceId] : null;
                const acceptKey = `accept-${invitation.id || invitation.workspaceId}`;
                const declineKey = `decline-${invitation.id || invitation.workspaceId}`;
                const roles = membershipRoleNames(invitation);
                const privileges = membershipPrivilegeNames(invitation);
                return (
                  <List.Item className="olreadia-flow-list-item">
                    <Card className="olreadia-flow-card">
                      <div className="olreadia-flow-card__top">
                        <span className="olreadia-flow-card__icon"><TeamOutlined /></span>
                        <div>
                          <Title level={4}>{workspace?.name || "Workspace"}</Title>
                          <Text>
                            {readable(workspace?.product, "Olreadia")} • {readable(workspace?.accountType, "Workspace")}
                          </Text>
                        </div>
                        <Tag color={statusColor(invitation.membershipStatus)}>
                          {readable(invitation.membershipStatus, "Pending")}
                        </Tag>
                      </div>
                      <div className="olreadia-flow-detail-grid">
                        <div>
                          <Text>Member</Text>
                          <strong>{membershipUserName(invitation)}</strong>
                        </div>
                        <div>
                          <Text>Email</Text>
                          <strong>{membershipUserEmail(invitation) || "Not set"}</strong>
                        </div>
                        <div>
                          <Text>Invited</Text>
                          <strong>{formatDateTime(invitation.createdAt) || "Not set"}</strong>
                        </div>
                        <div>
                          <Text>Roles</Text>
                          <strong>{roles.map(readable).join(", ") || "Member"}</strong>
                        </div>
                        <div>
                          <Text>Privileges</Text>
                          <strong>{privileges.map(readable).join(", ") || "None"}</strong>
                        </div>
                      </div>
                      <div className="olreadia-flow-actions">
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          loading={processingKey === declineKey}
                          disabled={Boolean(processingKey)}
                          onClick={() => respond(invitation, "decline")}
                        >
                          Decline
                        </Button>
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          loading={processingKey === acceptKey}
                          disabled={Boolean(processingKey)}
                          onClick={() => respond(invitation, "accept")}
                        >
                          Accept Invitation
                        </Button>
                      </div>
                    </Card>
                  </List.Item>
                );
              }}
            />
          ) : (
            <Card className="olreadia-flow-card">
              <Empty
                image={<InboxOutlined style={{ fontSize: 44, color: "#0d8abc" }} />}
                description="No pending invitations"
              >
                <Button onClick={() => navigate(paths.workspaces)}>Review workspaces</Button>
              </Empty>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}
